import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { Heart, Users, User, Baby, UserCircle, Send, CheckCircle2, BookOpen, Sparkles, Sun, Star, Book, Flame, Leaf, Home as HomeIcon, ThumbsUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const ICON_MAP = { Heart, Users, Home: HomeIcon, UserCircle, Sparkles, Sun, Star, Book, Flame, Leaf };

const feelings = [
  { id: 'sad', name: 'Sad', color: '#5B7C99', emoji: '💧' },
  { id: 'anxious', name: 'Anxious', color: '#E8C265', emoji: '🌫' },
  { id: 'angry', name: 'Angry', color: '#E57A44', emoji: '🔥' },
  { id: 'confused', name: 'Confused', color: '#78A18E', emoji: '🤔' },
  { id: 'hopeful', name: 'Hopeful', color: '#5B7C99', emoji: '☀️' },
  { id: 'hurt', name: 'Hurt', color: '#E57A44', emoji: '💔' }
];

const whoAbout = [
  { id: 'spouse', name: 'Spouse/Partner', icon: Heart },
  { id: 'family', name: 'Family', icon: Users },
  { id: 'friend', name: 'Friend', icon: UserCircle },
  { id: 'child', name: 'Child', icon: Baby },
  { id: 'self', name: 'Myself', icon: User }
];

export default function Feelings() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedFeeling, setSelectedFeeling] = useState(null);
  const [selectedWho, setSelectedWho] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [reqTopic, setReqTopic] = useState('');
  const [reqDescription, setReqDescription] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [reqSubmitted, setReqSubmitted] = useState(false);
  const [communityRequests, setCommunityRequests] = useState([]);
  const [votingKey, setVotingKey] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allGuides, setAllGuides] = useState([]);

  useEffect(() => {
    let ignore = false;
    api.get('/categories')
      .then(({ data }) => { if (!ignore) setCategories(Array.isArray(data) ? data : []); })
      .catch(() => {});
    api.get('/feelings')
      .then(({ data }) => { if (!ignore) setAllGuides(Array.isArray(data) ? data : []); })
      .catch(() => { if (!ignore) setAllGuides([]); });
    return () => { ignore = true; };
  }, []);

  const handleFeelingSelect = (feeling) => {
    setSelectedFeeling(feeling);
    setStep(2);
  };

  const buildGuide = (feeling, who, g, options = []) => ({
    title: `Guidance for ${feeling.name} about ${who.name}`,
    topic: g.topic || null,
    content: g.guidance,
    scriptures: g.scriptures || [],
    options,
    empty: false,
  });

  const showGuidance = async (feeling, who) => {
    const emptyGuide = { title: `Guidance for ${feeling.name} about ${who.name}`, content: null, scriptures: [], options: [], empty: true };
    try {
      const { data } = await api.get(`/feelings?feeling=${feeling.id}&who_about=${who.id}`);
      if (!data || data.length === 0) return setGuidance(emptyGuide);
      if (data.length === 1) return setGuidance(buildGuide(feeling, who, data[0]));
      setGuidance({ title: `Guidance for ${feeling.name} about ${who.name}`, options: data, pickTopic: true, empty: false, scriptures: [] });
    } catch (error) {
      setGuidance(emptyGuide);
    }
  };

  const pickTopicGuide = (g) => {
    setGuidance(buildGuide(selectedFeeling, selectedWho, g, guidance?.options || []));
  };

  const backToTopics = () => {
    setGuidance((prev) => ({ ...prev, pickTopic: true, topic: null, content: null, scriptures: [] }));
  };

  const reset = () => {
    setStep(1);
    setSelectedFeeling(null);
    setSelectedWho(null);
    setGuidance(null);
    setReqTopic('');
    setReqDescription('');
    setReqSubmitting(false);
    setReqSubmitted(false);
    setCommunityRequests([]);
  };

  // Backend expects relationship key 'myself' but our UI uses 'self'
  const relationshipKey = (whoId) => (whoId === 'self' ? 'myself' : whoId);

  // Open an archived guide — pre-fills the picker + jumps to the guidance view
  const openArchiveGuide = (guide) => {
    const feeling = feelings.find(f => f.id === guide.feeling) || { id: guide.feeling, name: guide.feeling, color: '#78A18E', emoji: '🌿' };
    const whoId = guide.who_about === 'myself' ? 'self' : guide.who_about;
    const who = whoAbout.find(w => w.id === whoId) || { id: whoId, name: whoId, icon: UserCircle };
    setSelectedFeeling(feeling);
    setSelectedWho(who);
    setStep(3);
    const siblings = allGuides.filter(x => x.feeling === guide.feeling && x.who_about === guide.who_about);
    setGuidance(buildGuide(feeling, who, guide, siblings.length > 1 ? siblings : []));
    loadCommunityRequests(feeling, who);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadCommunityRequests = useCallback(async (feeling, who) => {
    try {
      const { data } = await api.get(`/journey/requests/public?feeling=${feeling.id}&relationship=${relationshipKey(who.id)}`);
      setCommunityRequests(Array.isArray(data) ? data : []);
    } catch {
      setCommunityRequests([]);
    }
  }, []);

  const handleWhoSelect = async (who) => {
    setSelectedWho(who);
    setStep(3);
    await Promise.all([
      showGuidance(selectedFeeling, who),
      loadCommunityRequests(selectedFeeling, who),
    ]);
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFeeling || !selectedWho) return;
    const topic = reqTopic.trim();
    if (!topic) {
      toast.error("Please share what you're facing so Genesis knows what to write about.");
      return;
    }
    setReqSubmitting(true);
    try {
      await api.post('/journey/request', {
        feeling: selectedFeeling.id,
        relationship: relationshipKey(selectedWho.id),
        topic,
        description: reqDescription.trim(),
      });
      setReqSubmitted(true);
      toast.success("Your request was sent. We'll email you when it's written.");
      // Refresh the community list so this person's request appears immediately
      loadCommunityRequests(selectedFeeling, selectedWho);
    } catch (error) {
      const msg = formatApiErrorDetail(error?.response?.data?.detail) || 'Could not send your request. Please try again.';
      toast.error(msg);
    } finally {
      setReqSubmitting(false);
    }
  };

  const handleVote = async (topic) => {
    if (!user) {
      toast.error('Log in to vote on community requests.');
      return;
    }
    if (!selectedFeeling || !selectedWho) return;
    setVotingKey(topic.topic_key);
    // Optimistic update
    setCommunityRequests((prev) =>
      prev.map((r) =>
        r.topic_key === topic.topic_key
          ? { ...r, voted: !r.voted, upvotes: r.upvotes + (r.voted ? -1 : 1) }
          : r,
      ),
    );
    try {
      await api.post('/journey/request/vote', {
        feeling: selectedFeeling.id,
        relationship: relationshipKey(selectedWho.id),
        topic: topic.topic,
      });
    } catch (error) {
      // Rollback on error
      setCommunityRequests((prev) =>
        prev.map((r) =>
          r.topic_key === topic.topic_key
            ? { ...r, voted: !r.voted, upvotes: r.upvotes + (r.voted ? -1 : 1) }
            : r,
        ),
      );
      const msg = formatApiErrorDetail(error?.response?.data?.detail) || 'Could not save your vote.';
      toast.error(msg);
    } finally {
      setVotingKey(null);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Serene ocean backdrop */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'saturate(0.85) brightness(1.05)',
        }}
      />
      {/* Warm cream + soft-blue wash so text stays serene and legible */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          background: 'linear-gradient(180deg, rgba(253, 251, 247, 0.82) 0%, rgba(217, 231, 234, 0.72) 45%, rgba(253, 251, 247, 0.88) 100%)',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
      <Header />

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
            Find Guidance for Your Feelings
          </h1>
          <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            Let us help you navigate your emotions with biblical wisdom
          </p>
          {step === 1 && (
            <div className="max-w-2xl mx-auto mt-6 px-4 py-3 rounded-2xl" style={{ backgroundColor: '#F3EFE7' }} data-testid="feelings-intro-note">
              <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7, fontStyle: 'italic' }}>
                Every response here is written prayerfully and grows over time as new encouragement is added. If you're walking through something more specific, share it on the <Link to="/prayer-wall" className="underline" style={{ color: '#E57A44', fontWeight: 600 }} data-testid="feelings-prayer-wall-link">prayer wall</Link> or explore the article categories.
              </p>
            </div>
          )}
        </div>

        {step === 1 && (
          <div>
            <h2 className="text-2xl font-semibold text-center mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              How are you feeling?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {feelings.map((feeling) => (
                <button
                  key={feeling.id}
                  onClick={() => handleFeelingSelect(feeling)}
                  className="p-6 rounded-3xl transition-all duration-300 hover:-translate-y-2 hover:scale-105"
                  style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
                  data-testid={`feeling-${feeling.id}`}
                >
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl" style={{ backgroundColor: `${feeling.color}20` }}>
                    <span aria-hidden="true">{feeling.emoji}</span>
                  </div>
                  <span className="text-xl font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{feeling.name}</span>
                </button>
              ))}
            </div>
            <FeelingsLibrary allGuides={allGuides} feelings={feelings} whoAbout={whoAbout} openArchiveGuide={openArchiveGuide} />
          </div>
        )}

        {step === 2 && (
          <div>
            <button
              onClick={reset}
              className="mb-8 px-4 py-2 rounded-xl"
              style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }}
            >
              ← Back
            </button>
            <h2 className="text-2xl font-semibold text-center mb-8" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              Who is this about?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {whoAbout.map((who) => {
                const Icon = who.icon;
                return (
                  <button
                    key={who.id}
                    onClick={() => handleWhoSelect(who)}
                    className="p-6 rounded-3xl transition-all duration-300 hover:-translate-y-2"
                    style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }}
                    data-testid={`who-${who.id}`}
                  >
                    <Icon className="w-12 h-12 mx-auto mb-4" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
                    <span className="text-lg font-semibold block" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{who.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && guidance && (
          <div>
            <button
              onClick={reset}
              className="mb-8 px-4 py-2 rounded-xl"
              style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }}
              data-testid="reset-button"
            >
              ← Start Over
            </button>

            {/* Guidance card — filled OR empty state */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(44, 39, 36, 0.05)', border: '1px solid #F0EBE0' }}>
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', letterSpacing: '0.12em' }}
                >
                  Guidance
                </span>
                <div className="h-px flex-1" style={{ backgroundColor: '#E6E2D8' }} />
              </div>
              <h2
                className="text-xl sm:text-2xl font-semibold mb-4"
                style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', letterSpacing: '-0.01em' }}
              >
                {guidance.title}
              </h2>
              {guidance.topic && (
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A4418', color: '#C2582A' }} data-testid="guidance-topic-label">
                    Topic: {guidance.topic}
                  </span>
                  {guidance.options?.length > 1 && (
                    <button onClick={backToTopics} className="text-xs font-semibold underline" style={{ fontFamily: 'Manrope, sans-serif', color: '#5B7C99' }} data-testid="switch-topic-btn">
                      Choose a different topic ({guidance.options.length})
                    </button>
                  )}
                </div>
              )}
              {!guidance.empty && (
                <div className="mb-5" data-testid="suggest-tab-link">
                  <Link
                    to="/topics"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      backgroundColor: '#E8C26525',
                      color: '#8B6A0A',
                      fontSize: '12px',
                      fontWeight: 600,
                      border: '1px solid rgba(232, 194, 101, 0.35)',
                    }}
                    data-testid="suggest-topic-pill"
                  >
                    <span aria-hidden="true">💡</span>
                    <span>Have another topic in mind? Suggest it here →</span>
                  </Link>
                </div>
              )}
              {guidance.pickTopic ? (
                <TopicPicker options={guidance.options} onPick={pickTopicGuide} />
              ) : guidance.empty ? (
                <div data-testid="guidance-empty-state">
                  <p className="text-base font-medium mb-1.5" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                    No article on this yet
                  </p>
                  <p className="text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.65 }}>
                    But this page is for you. Be the first to suggest a specific topic and help shape what Genesis writes next — you'll get an email the moment it's published.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-base mb-6" style={{ fontFamily: 'Lora, serif', color: '#3D3833', lineHeight: 1.8 }}>
                    {guidance.content}
                  </p>
                  {guidance.scriptures.length > 0 && (
                    <div className="pl-4 py-2" style={{ borderLeft: '2px solid #5B7C99' }}>
                      <p
                        className="text-xs font-semibold uppercase mb-2"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#5B7C99', letterSpacing: '0.1em' }}
                      >
                        Scripture
                      </p>
                      {guidance.scriptures.map((scripture, i) => (
                        <blockquote
                          key={i}
                          className="mb-2 last:mb-0 italic text-sm"
                          style={{ fontFamily: 'Lora, serif', color: '#3D5A73', lineHeight: 1.65 }}
                        >
                          "{scripture.text}"{scripture.reference ? ` — ${scripture.reference}` : ''}
                        </blockquote>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Suggest — full form on empty state only (filled state has the pill inside guidance card) */}
            {guidance.empty && (
              <div className="mt-8 p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }} data-testid="suggest-section">
                {reqSubmitted ? (
                  <div
                    className="p-6 rounded-2xl flex items-start gap-4"
                    style={{ backgroundColor: '#78A18E15', border: '1px solid #78A18E40' }}
                    data-testid="request-submitted"
                  >
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: '#4B6B5C' }} />
                    <div>
                      <p className="font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                        Thank you for trusting us with this.
                      </p>
                      <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#4B6B5C', lineHeight: 1.7 }}>
                        Genesis has been notified. As soon as an article addressing this is published, you'll receive an email at <strong>{user?.email}</strong>.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Link
                          to="/prayer-wall"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', color: '#4B6B5C', fontWeight: 600, border: '1px solid #78A18E40' }}
                          data-testid="request-cta-prayer-wall"
                        >
                          Visit the Prayer Wall
                        </Link>
                        <button
                          onClick={reset}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                          data-testid="request-cta-restart"
                        >
                          Explore Another Feeling
                        </button>
                      </div>
                    </div>
                  </div>
                ) : !user ? (
                  <div className="text-center" data-testid="request-login-prompt">
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                      Want something more specific?
                    </h3>
                    <p className="mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>
                      Sign in to suggest a topic — you'll get an email the moment Genesis writes about it.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                        data-testid="request-login-button"
                      >
                        Log In
                      </Link>
                      <Link
                        to="/register"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', color: '#E57A44', fontWeight: 600, border: '1px solid #E57A44' }}
                        data-testid="request-register-button"
                      >
                        Create Account
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleRequestSubmit} className="space-y-4" data-testid="request-form">
                    <div>
                      <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                        Be the first to suggest a topic
                      </h3>
                      <p className="text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
                        Share what you're walking through and Genesis will write something for it. You'll get an email the moment it's live.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="req-topic"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}
                      >
                        Title <span style={{ color: '#E57A44' }}>*</span>
                      </label>
                      <input
                        id="req-topic"
                        type="text"
                        maxLength={200}
                        value={reqTopic}
                        onChange={(e) => setReqTopic(e.target.value)}
                        placeholder={`e.g. Feeling ${selectedFeeling?.name?.toLowerCase()} about ${selectedWho?.name?.toLowerCase()}...`}
                        className="w-full px-4 py-3 rounded-xl focus:outline-none"
                        style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8', color: '#2C2A29' }}
                        data-testid="request-topic-input"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="req-description"
                        className="block text-sm font-semibold mb-2"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2A29' }}
                      >
                        Description <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <textarea
                        id="req-description"
                        rows={4}
                        maxLength={2000}
                        value={reqDescription}
                        onChange={(e) => setReqDescription(e.target.value)}
                        placeholder="Share as much or as little as feels right. Your words stay private between you and Genesis."
                        className="w-full px-4 py-3 rounded-xl focus:outline-none resize-none"
                        style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8', color: '#2C2A29', lineHeight: 1.6 }}
                        data-testid="request-description-input"
                      />
                      <p className="text-xs mt-1.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        {reqDescription.length}/2000
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={reqSubmitting}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600, fontSize: '14px' }}
                        data-testid="request-submit-button"
                      >
                        <Send className="w-4 h-4" strokeWidth={2} />
                        <span>{reqSubmitting ? 'Sending…' : 'Send this to Genesis'}</span>
                      </button>
                      <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                        We'll email you at <strong style={{ color: '#595553' }}>{user.email}</strong> the moment it's written.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Community requests — vote to encourage what Genesis writes next */}
            {communityRequests.length > 0 && (
              <div className="mt-8 p-8 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)' }} data-testid="community-requests-section">
                <div className="mb-5">
                  <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                    Others are asking for…
                  </h3>
                  <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
                    Tap the heart to encourage what should be written next. Popular topics rise to the top.
                  </p>
                </div>
                <ul className="space-y-3">
                  {communityRequests.map((r) => (
                    <li
                      key={r.topic_key}
                      className="flex items-center gap-4 p-4 rounded-2xl"
                      style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8' }}
                      data-testid={`community-request-${r.topic_key}`}
                    >
                      <button
                        onClick={() => handleVote(r)}
                        disabled={votingKey === r.topic_key}
                        aria-pressed={r.voted}
                        aria-label={r.voted ? 'Remove your vote' : 'Encourage this topic'}
                        className="flex flex-col items-center justify-center gap-0.5 shrink-0 w-14 h-14 rounded-2xl transition-all duration-300 disabled:opacity-60"
                        style={{
                          fontFamily: 'Manrope, sans-serif',
                          backgroundColor: r.voted ? '#E57A44' : '#FFFFFF',
                          color: r.voted ? '#FDFBF7' : '#E57A44',
                          border: `1.5px solid ${r.voted ? '#E57A44' : '#E57A4460'}`,
                        }}
                        data-testid={`community-vote-${r.topic_key}`}
                      >
                        <ThumbsUp className="w-4 h-4" strokeWidth={2} fill={r.voted ? '#FDFBF7' : 'none'} />
                        <span className="text-xs font-bold leading-none">{r.upvotes}</span>
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm leading-snug" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                          {r.topic}
                        </p>
                        {r.count > 1 && (
                          <p className="text-xs mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E' }}>
                            {r.count} people have asked
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Explore existing articles — warm, inviting category browser */}
            {categories.length > 0 && (
              <div
                className="mt-8 p-8 rounded-3xl relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #FDFBF7 0%, #F3EFE7 60%, #EDE4CF 100%)',
                  boxShadow: '0 12px 40px rgba(232, 194, 101, 0.15)',
                  border: '1px solid rgba(232, 194, 101, 0.25)',
                }}
                data-testid="explore-articles-section"
              >
                <div
                  className="absolute -top-8 -right-8 opacity-20 pointer-events-none"
                  aria-hidden
                >
                  <BookOpen className="w-40 h-40" style={{ color: '#E57A44' }} strokeWidth={1} />
                </div>

                <div className="relative">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="flex items-center justify-center rounded-2xl"
                      style={{ width: 44, height: 44, backgroundColor: 'rgba(255,255,255,0.9)' }}
                    >
                      <BookOpen className="w-5 h-5" style={{ color: '#E57A44' }} strokeWidth={2} />
                    </div>
                    <h3
                      className="text-2xl font-semibold leading-tight"
                      style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}
                    >
                      Explore what Genesis has already written
                    </h3>
                  </div>
                  <p
                    className="mb-6 max-w-2xl"
                    style={{ fontFamily: 'Lora, serif', color: '#5C4515', fontSize: '15px', lineHeight: 1.7, fontStyle: 'italic' }}
                  >
                    Check out articles waiting for you — there may already be something written for exactly where you are. Pick a category below and step in.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {categories.map((c) => {
                      const IconComp = ICON_MAP[c.icon] || Heart;
                      return (
                        <Link
                          key={c.id}
                          to={`/category/${c.id}`}
                          className="group inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            backgroundColor: '#FFFFFF',
                            color: '#2C2724',
                            fontWeight: 600,
                            fontSize: '14px',
                            border: '1px solid rgba(232, 194, 101, 0.4)',
                            boxShadow: '0 4px 12px rgba(44, 39, 36, 0.05)',
                          }}
                          data-testid={`explore-category-${c.id}`}
                        >
                          <span
                            className="flex items-center justify-center rounded-full"
                            style={{ width: 28, height: 28, backgroundColor: '#E57A4415' }}
                          >
                            <IconComp className="w-4 h-4" style={{ color: '#E57A44', strokeWidth: 2 }} />
                          </span>
                          <span>{c.name}</span>
                          <ArrowRight
                            className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                            style={{ color: '#E57A44' }}
                            strokeWidth={2.5}
                          />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
      </div>
    </div>
  );
}


/* ── The Feelings Library — archive of every guide Genesis has written ── */
function FeelingsLibrary({ allGuides, feelings, whoAbout, openArchiveGuide }) {
  const [filter, setFilter] = useState('all');
  if (!allGuides || allGuides.length === 0) return null;

  const feelingById = Object.fromEntries(feelings.map(f => [f.id, f]));
  const whoById = { ...Object.fromEntries(whoAbout.map(w => [w.id, w])), myself: whoAbout.find(w => w.id === 'self') };
  const visible = filter === 'all' ? allGuides : allGuides.filter(g => g.feeling === filter);

  const counts = feelings.reduce((acc, f) => {
    acc[f.id] = allGuides.filter(g => g.feeling === f.id).length;
    return acc;
  }, {});

  return (
    <div className="mt-12" data-testid="feelings-library">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#B8891A', letterSpacing: '0.15em' }}>The Feelings Library</p>
        <h2 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
          Every word Genesis has written, one tap away
        </h2>
        <p className="mt-2 text-sm max-w-xl mx-auto" style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic', lineHeight: 1.7 }}>
          Browse the full collection of feelings and situations — tap any card to open the guide.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-6">
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} testid="lib-filter-all">
          All · {allGuides.length}
        </FilterPill>
        {feelings.filter(f => counts[f.id] > 0).map(f => (
          <FilterPill key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testid={`lib-filter-${f.id}`} color={f.color}>
            <span className="mr-1">{f.emoji}</span>{f.name} · {counts[f.id]}
          </FilterPill>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map(g => {
          const f = feelingById[g.feeling] || { name: g.feeling, color: '#78A18E', emoji: '🌿' };
          const w = whoById[g.who_about] || { name: g.who_about };
          const preview = (g.guidance || '').replace(/\s+/g, ' ').trim().slice(0, 160);
          return (
            <button
              key={g.id}
              onClick={() => openArchiveGuide(g)}
              className="text-left p-5 rounded-2xl transition-all duration-300 hover:-translate-y-1"
              style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderLeft: `4px solid ${f.color}` }}
              data-testid={`library-card-${g.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl" aria-hidden>{f.emoji}</span>
                <p className="text-sm font-semibold capitalize" style={{ fontFamily: 'Manrope, sans-serif', color: f.color }}>
                  Feeling {f.name} · about {w.name}
                </p>
              </div>
              {g.topic && (
                <span className="inline-block mb-2 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: `${f.color}18`, color: f.color }} data-testid={`library-card-topic-${g.id}`}>
                  {g.topic}
                </span>
              )}
              <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.6 }}>
                {preview}{(g.guidance || '').length > 160 ? '…' : ''}
              </p>
              <p className="mt-3 inline-flex items-center gap-1 text-xs font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44' }}>
                Read the full guidance <ArrowRight className="w-3 h-3" />
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid, color }) {
  const tint = color || '#E57A44';
  return (
    <button
      onClick={onClick}
      className="px-3.5 py-1.5 rounded-full text-xs font-semibold transition hover:-translate-y-0.5"
      style={{
        fontFamily: 'Manrope, sans-serif',
        backgroundColor: active ? tint : '#FFFFFF',
        color: active ? '#FFFFFF' : '#2C2724',
        border: `1px solid ${active ? tint : '#E6E2D8'}`,
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function TopicPicker({ options, onPick }) {
  return (
    <div data-testid="topic-picker">
      <p className="text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.65 }}>
        There are {options.length} guides for this. Choose the topic that speaks to what you're walking through:
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((g) => (
          <button
            key={g.id}
            onClick={() => onPick(g)}
            className="text-left p-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
            style={{ backgroundColor: '#FDFBF7', border: '1px solid #E6E2D8' }}
            data-testid={`topic-option-${g.id}`}
          >
            <p className="font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>{g.topic || 'General guidance'}</p>
            <p className="text-xs" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.5 }}>
              {(g.guidance || '').replace(/\s+/g, ' ').trim().slice(0, 90)}…
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
